// ===========================================
// BUSTED! BLACKJACK - WELCOME PAGE (NEW USERS)
// ===========================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Lottie from 'lottie-react';
import { useAuthStore } from '../stores';
import { api } from '../api';
import { ECONOMY_CONSTANTS } from '@busted-blackjack/shared';
import { APP_VERSION } from '../version';
import spinningCoinAnimation from '../assets/spinning-coin.json';

export function WelcomePage() {
  const navigate = useNavigate();
  const { user, fetchUser, isAuthenticated, isLoading } = useAuthStore();

  const [username, setUsername] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Pre-fill with current display name (includes discriminator if present)
  useEffect(() => {
    if (user) {
      setUsername(user.displayName);
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Extract base username (remove discriminator if user typed one)
    const baseUsername = username.trim().split('#')[0];

    if (!baseUsername) {
      setError('Please enter a username');
      return;
    }

    if (baseUsername === user?.username) {
      // No change, just continue
      navigate('/');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await api.users.changeUsername(baseUsername);

      if (result.success) {
        // Refresh user data
        await fetchUser();
        navigate('/');
      } else {
        setError(result.error?.message || 'Failed to change username');
      }
    } catch {
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    navigate('/');
  };

  if (isLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 px-4">
      <div className="max-w-md w-full">
        {/* Welcome Header */}
        <div className="text-center mb-8">
          <span className="text-6xl">🎉</span>
          <h1 className="text-3xl font-bold text-white mt-4">
            Welcome to<br />BUSTED! Blackjack
          </h1>
          <p className="text-gray-400 mt-2">Let's get you set up</p>
        </div>

        {/* Setup Card */}
        <div className="card">
          <div className="card-body space-y-6">
            {/* Starting Chips */}
            <div className="bg-casino-gold-500/10 border border-casino-gold-500/30 rounded-lg p-4 text-center">
              <p className="text-casino-gold-400 text-sm font-bold">CONGRATULATIONS!</p>
              <p className="text-casino-gold-400 text-sm">You've received a signing bonus!</p>
              <div className="flex items-center justify-center gap-2 my-2">
                <p className="text-3xl font-bold text-casino-gold-400">
                  {ECONOMY_CONSTANTS.STARTING_CHIPS.toLocaleString('en-US')} B-Bucks
                </p>
                <Lottie
                  animationData={spinningCoinAnimation}
                  loop={true}
                  className="w-10 h-10"
                />
              </div>
              <p className="text-casino-gold-400 text-sm">Enjoy your BUSTED! Bucks.</p>
            </div>

            {/* Username Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-2">
                  Choose Your Username
                </label>
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="input"
                  placeholder="Enter username"
                  minLength={3}
                  maxLength={25}
                />
                <p className="mt-1 text-xs text-gray-500">
                  3-20 characters. Letters, numbers, and underscores only.
                </p>
                {/* Table URL Preview */}
                {user.tableSlug && (
                  <p className="mt-2 text-xs text-casino-gold-400">
                    Your personal table:{' '}
                    <span className="font-mono text-white">
                      bustedblackjack.com/table/
                      <span className="text-casino-gold-300">
                        {user.tableSlug}
                      </span>
                    </span>
                  </p>
                )}
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3 text-red-400 text-sm">
                  {error}
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleSkip}
                  className="btn-secondary flex-1"
                >
                  Keep "{user.displayName}"
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary flex-1"
                >
                  {isSubmitting ? 'Saving...' : 'Save & Continue'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Info */}
        <p className="text-center text-gray-600 text-sm mt-6">
          You can always change your username later from settings
        </p>

        {/* Footer */}
        <footer className="mt-8 text-center text-sm text-gray-500">
          <span className="block">Must be 18+</span>
          <span className="block">Play Money Only • Not Real Gambling</span>
          <span className="block mt-2 text-xs text-gray-600">BUSTED! Blackjack v{APP_VERSION}</span>
        </footer>
      </div>
    </div>
  );
}

export default WelcomePage;